import React, { useEffect, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import * as LoadingIcons from "react-loading-icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteConfirmModal from "./DeleteConfirmModal";
import ClientForum from "./ClientForum";
import { base_url, deleteClient, useGetClients } from "../services/ClientService";
import ClientModel from "../models/ClientModel";
import { useSelector } from "react-redux";

const ClientsList = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null); // <- nouvel état
    const [searchTerm, setSearchTerm] = useState("");
    const token = useSelector(state => state.auth.token);
    const { clients, loading, pagination, fetchClients, page, setPage, setClients, setLoading, setPagination } = useGetClients();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [clientToDelete, setClientToDelete] = useState(null);
    const handleAddClient = () => {
        setEditingClient(null); // pas de client => ajout
        setIsModalOpen(true);
    };

    // Pour ouvrir le modal en édition
    const handleEditClient = (client) => {
        setEditingClient(client); // client à modifier
        setIsModalOpen(true);
    };
    useEffect(() => {
        fetchClients(page);
    }, [page, fetchClients]);


    useEffect(() => {
        const fetchSearchClients = async (query) => {
            if (!token) return;
            setLoading(true);
            try {
                const response = await fetch(`${base_url}clients/search?query=${encodeURIComponent(query)}`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (!response.ok) throw new Error("Erreur lors de la recherche");
                const data = await response.json();
                setClients(data.clients.map((item) => new ClientModel(item)));
                setPagination({
                    total: data.total,
                    per_page: data.per_page,
                    current_page: data.current_page,
                    last_page: Math.ceil(data.total / data.per_page),
                });
            } catch (err) {
                console.error(err);
                setClients([]);
            } finally {
                setLoading(false);
            }
        };

        const delayDebounce = setTimeout(() => {
            if (searchTerm.trim() === "") {
                fetchClients(1);
            } else {
                fetchSearchClients(searchTerm);
            }
        }, 500);

        return () => clearTimeout(delayDebounce);
    }, [searchTerm, fetchClients, token, setClients, setPagination, setLoading]);



    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.last_page) {
            setPage(newPage);
            const element = document.getElementById("scroll_clients_pagination");
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
    };
    const handleDeleteClick = (client) => {
        setClientToDelete(client);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!clientToDelete) return;
        try {
            const result = await deleteClient(clientToDelete.id);
            toast.success(result.message);
            fetchClients(page);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setClientToDelete(null);
        }
    };

    return (
        <section
            className=" pb-4 flex flex-col gap-3 justify-center lg:px-4 px-2 w-full"
            id="scroll_clients_pagination"
        >
            <ToastContainer position="top-right" autoClose={3000} />
            <DeleteConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                clientName={clientToDelete?.nom_affichage}
            />
            <ClientForum
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onClientSaved={(savedClient) => {
                    // Toast déjà géré dans le modal
                    fetchClients(page); // recharge la liste après ajout ou mise à jour
                }}
                client={editingClient} // client à modifier ou null pour ajout
            />


            <div className="flex flex-col items-start gap-1 justify-between w-full">
                <h1 className="font-bold font-Poppins lg:text-2xl text-xl text-color11 whitespace-nowrap">
                    Liste des Clients
                </h1>

                <div className="flex flex-row w-full  items-center flex-1 gap-2 py-2">
                    <input
                        type="text"
                        placeholder="Chercher un client par nom..."
                        className="flex-1 px-4 py-2 border border-color1 rounded-md focus:outline-none focus:ring-2 focus:ring-color1 focus:border-color1"
                        value={searchTerm} // lie l'input à l'état searchTerm
                        onChange={(e) => setSearchTerm(e.target.value)} // met à jour searchTerm à chaque frappe
                    />


                    <button
                        className="bg-color3 hover:bg-green-600 text-white px-3 py-2 rounded-lg flex items-center gap-1 text-xs"
                        onClick={() => handleAddClient()}
                    >
                        <FaEdit /> Ajouter un client
                    </button>
                </div>
            </div>




            <div className="overflow-x-auto shadow-md rounded-xl border border-gray-200 bg-white">
                <table className="w-full text-sm text-left border-collapse">
                    <thead className="bg-color1 text-color2 uppercase text-xs font-semibold">
                        <tr>
                            <th className="px-3 py-2 w-[40px]">#</th>
                            <th className="px-3 py-2 w-[120px]">Code Client</th>
                            <th className="px-3 py-2 w-[180px]">Raison Social</th>
                            <th className="px-3 py-2 w-[150px]">Nom & Prénom</th>
                            <th className="px-3 py-2 w-[200px]">Email</th>
                            <th className="px-3 py-2 w-[120px]">Téléphone</th>
                            <th className="px-3 py-2 w-[200px]">Adresse</th>
                            <th className="px-3 py-2 w-[120px]">SIRET</th>
                            <th className="px-3 py-2 w-[120px]">N° TVA</th>
                            <th className="px-3 py-2 text-center w-[160px]">Actions</th>
                        </tr>
                    </thead>
                    <tbody  >
                        {loading ? (
                            <tr>
                                <td colSpan="10" className="h-[450px] text-center">
                                    <div className="flex justify-center items-center">
                                        <LoadingIcons.SpinningCircles
                                            stroke="#2218eeff"   // couleur principale
                                            fill="#2218eeff"
                                            strokeWidth={3}
                                            height={40}
                                            width={40}
                                        />
                                        <span className="ml-3 text-gray-600 font-medium">
                                            Chargement des clients...
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        ) : clients && clients.length > 0 ? (
                            clients.map((client, index) => (
                                <tr key={client.id} className="border-b hover:bg-gray-50 transition">
                                    <td className="px-3 py-2 text-center">
                                        {index + 1 + (pagination.current_page - 1) * pagination.per_page}
                                    </td>

                                    <td className="px-3 py-2 break-words">{client.code_client}</td>
                                    <td className="px-3 py-2 break-words">{client.nom_affichage}</td>
                                    <td className="px-3 py-2 break-words">{client.nom_contact || "-"}</td>
                                    <td className="px-3 py-2 break-words">{client.email || "-"}</td>
                                    <td className="px-3 py-2">{client.telephone || "-"}</td>
                                    <td className="px-3 py-2 break-words">
                                        {client.code_postal && client.ville
                                            ? `${client.code_postal} ${client.ville}`
                                            : client.ville || "-"}
                                    </td>
                                    <td className="px-3 py-2 break-words">{client.siret || "-"}</td>
                                    <td className="px-3 py-2 break-words">{client.numero_tva || "-"}</td>
                                    <td className="px-3 py-2 text-center">
                                        <div className="flex flex-col gap-1 justify-center">
                                            <button

                                                onClick={() => handleEditClient(client)}
                                                className="bg-color5 hover:bg-color5/90 text-center flex justify-center
                                                 text-white px-2 py-1 rounded-lg flex items-center gap-1 text-xs">
                                                <FaEdit /> Modifier
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(client)}
                                                className="bg-color16 hover:bg-red-600 text-center flex justify-center
                                                text-white px-2 py-1 rounded-lg flex items-center gap-1 text-xs">
                                                <FaTrash /> Supprimer
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="10" className="py-4 text-center text-gray-500">
                                    Aucun client trouvé.
                                </td>
                            </tr>
                        )}
                    </tbody>


                </table>
            </div>

            {/* Pagination */}
            <div className="flex w-full justify-center items-center gap-3 mt-4">
                <button
                    disabled={pagination.current_page === 1}
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    className={`px-4 py-1 rounded-lg text-white text-sm font-semibold transition
            ${pagination.current_page === 1
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-color1 hover:bg-color6 cursor-pointer"
                        }`}
                >
                    Précédent
                </button>

                <span className="text-color11 font-medium text-sm">
                    {pagination.current_page} / {pagination.last_page}
                </span>

                <button
                    disabled={pagination.current_page === pagination.last_page}
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    className={`px-4 py-1 rounded-lg text-white text-sm font-semibold transition
            ${pagination.current_page === pagination.last_page
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-color1 hover:bg-color6 cursor-pointer"
                        }`}
                >
                    Suivant
                </button>
            </div>
        </section>

    );
};

export default ClientsList;
